import React, { useCallback, useMemo } from 'react'
import classNames from 'classnames'
import { Close } from '@/components/svg'
import useWindowSize from '@/services/hooks/useWindowSize'
import Button from '../Button'
import Modal, { Props as ModalProps } from '../Modal/Modal'
import Type from '../Type'

export interface Props extends ModalProps {
  fillHeightOnMobile?: boolean
  dismissable?: boolean
  width?: string
}

export const CardModal = ({
  setIsOpen,
  fillHeightOnMobile = false,
  dismissable = true,
  width,
  children,
  ...rest
}: Props) => {
  const { innerWidth, innerHeight } = useWindowSize()
  const isMobile = useMemo(() => innerWidth <= 640, [innerWidth])
  const handleClose = useCallback(() => setIsOpen(false), [setIsOpen])

  const classes = classNames(
    {
      'relative bg-white sm:rounded sm:shadow-card overflow-y-auto h-full max-w-full flex flex-col':
        true,
      'w-screen rounded-tl-[1.5rem] rounded-tr-[1.5rem]': isMobile,
      'w-[464px]': !isMobile && !width,
    },
    !!width && !isMobile ? width : ''
  )

  return (
    <Modal
      {...rest}
      setIsOpen={setIsOpen}
      overlayColor="rgba(0,0,0,0.25)"
      transition={isMobile ? 'slideUp' : 'scaleIn'}
      disableAutoClose={!dismissable}
    >
      {dismissable && (
        <Button
          variant="primary"
          color="lightGray"
          size="xs"
          rounded={false}
          className="flex items-center justify-center p-0 absolute w-8 h-8 right-6 top-4 z-50 rounded-full sm:top-5"
          data-testid="card-modal-close"
          onClick={handleClose}
          aria-label="Close Dialog"
        >
          <Close className="h-4 w-4 shrink-0" />
        </Button>
      )}
      <div
        className={classes}
        style={{
          height: isMobile && fillHeightOnMobile ? innerHeight - 20 : undefined,
          maxHeight:
            isMobile && fillHeightOnMobile ? undefined : innerHeight - 20,
        }}
      >
        {children}
      </div>
    </Modal>
  )
}

CardModal.Title = Modal.Title

interface HeaderProps {
  children: React.ReactNode
  className?: string
}

const CardModalHeader = ({
  children,
  className = 'pl-6 pt-6 pb-2 pr-16',
}: HeaderProps) => (
  <CardModal.Title className={className}>
    <Type as="span" variant="title-sm" className="font-semibold">
      {children}
    </Type>
  </CardModal.Title>
)

CardModal.Header = CardModalHeader

interface BodyProps {
  children: React.ReactNode
  className?: string
}

const CardModalBody = ({
  children,
  className = 'pb-6 pt-4 px-6',
}: BodyProps) => <div className={className}>{children}</div>

CardModal.Body = CardModalBody

interface FooterProps {
  children?: React.ReactNode
  className?: string
}

const CardModalFooter = ({
  children,
  className = 'flex justify-between px-6 py-6',
}: FooterProps) => <div className={className}>{children}</div>

CardModal.Footer = CardModalFooter

export default CardModal
